import { useEffect, useState, useCallback, type FormEvent } from 'react'
import { Plus, X, ShieldCheck, ShieldAlert, Search, Sliders, Pencil, Trash2, RefreshCw } from 'lucide-react'
import api, { pesanGalat } from '../lib/api'
import Siapkan from './Siapkan'

type Lagu = {
  id: string; judul: string; artis: string; bahasa: string
  genre: string | null; tahun: number | null; nada_dasar: string | null
  durasi_detik: number | null
  sumber: string; status: string; status_lisensi: string
  lisensi_catatan?: string | null
  ada_lirik: boolean; lirik_sinkron: boolean; ada_video: boolean
  // 30 Okt: kategori Top ElGroup (kode kr_luar_genre untuk bahasanya).
  kategori?: string | null; kategori_manual?: boolean; bahasa_kategori?: string | null
}
type Kategori = { bahasa: string; kode: string; nama: string; aktif: boolean; jumlah: number; manual: number }
const NAMA_BAHASA: Record<string, string> = { id: 'Indonesia', en: 'Inggris', zh: 'China', ja: 'Jepang', ko: 'Korea' }

// 30 Okt: katalog dimuat per halaman (dulu dipatok 500 → katalog terbaca "500").
const PER_HAL = 200

const SUMBER: Record<string, string> = {
  lisensi: 'Lisensi penyedia',
  rekam_ulang: 'Rekam ulang',
  milik_sendiri: 'Milik sendiri',
  publik_domain: 'Publik domain',
}

const PIL_LISENSI: Record<string, string> = {
  aman:            'bg-gold/10 text-gold border-gold/30',
  belum_diperiksa: 'bg-white/5 text-text-3 border-border',
  perlu_dokumen:   'bg-amber-500/10 text-amber-400 border-amber-500/30',
  ditolak:         'bg-red-500/10 text-red-400 border-red-500/30',
}

const PIL_STATUS: Record<string, string> = {
  siap:     'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  draft:    'bg-white/5 text-text-3 border-border',
  nonaktif: 'bg-red-500/10 text-red-400 border-red-500/30',
}

export default function Katalog({ bolehKelola }: { bolehKelola: boolean }) {
  const [lagu, setLagu] = useState<Lagu[]>([])
  const [cari, setCari] = useState('')
  const [status, setStatus] = useState('')
  const [memuat, setMemuat] = useState(false)
  const [galat, setGalat] = useState('')
  const [formBuka, setFormBuka] = useState(false)
  const [ubahLagu, setUbahLagu] = useState<Lagu | null>(null)  // 21 Okt: edit data lagu
  const [siapkan, setSiapkan] = useState<string | null>(null)
  // 30 Okt (Zaldy: "ini max 500 padahal lebih"): dimuat per halaman, angka
  // kartu dari server atas SELURUH katalog (GET /api/lagu/ringkas).
  const [hal, setHal] = useState(0)
  // 30 Okt (Zaldy: "top elgroup kategorinya jg kita yg maping aja").
  const [kategori, setKategori] = useState('')
  const [daftarKat, setDaftarKat] = useState<Kategori[]>([])
  const [pilih, setPilih] = useState<Set<string>>(new Set())
  const [katMassal, setKatMassal] = useState('')
  const muatKat = useCallback(() => {
    api.get('/api/lagu/kategori').then(r => setDaftarKat(r.data ?? [])).catch(() => {})
  }, [])
  useEffect(() => { muatKat() }, [muatKat])
  const [ringkas, setRingkas] = useState<{ total: number; siap: number; perlu_lisensi: number; cocok: number } | null>(null)

  useEffect(() => { setHal(0) }, [cari, status, kategori])
  useEffect(() => { setPilih(new Set()) }, [cari, status, kategori, hal])

  const muat = useCallback(async () => {
    setMemuat(true); setGalat('')
    try {
      const p = new URLSearchParams({ limit: String(PER_HAL), offset: String(hal * PER_HAL) })
      if (cari.trim()) p.set('q', cari.trim())
      if (status) p.set('status', status)
      if (kategori) p.set('kategori', kategori)
      const pr = new URLSearchParams()
      if (cari.trim()) pr.set('q', cari.trim())
      if (status) pr.set('status', status)
      if (kategori) pr.set('kategori', kategori)
      const [r, rk] = await Promise.all([
        api.get(`/api/lagu?${p}`),
        api.get(`/api/lagu/ringkas?${pr}`).catch(() => null),
      ])
      setLagu(r.data ?? [])
      if (rk?.data) setRingkas(rk.data)
    } catch (e) {
      setGalat(pesanGalat(e, 'Gagal memuat katalog'))
    } finally { setMemuat(false) }
  }, [cari, status, hal, kategori])

  useEffect(() => { const t = setTimeout(muat, 250); return () => clearTimeout(t) }, [muat])

  // Menaikkan lagu ke 'siap' akan DITOLAK SERVER bila lisensinya belum 'aman'.
  // Pesannya sudah diterjemahkan di backend jadi kalimat — bukan nama
  // constraint Postgres, yang tidak berarti apa-apa bagi yang membacanya.
  // 21 Okt: hapus. 22 Okt (Zaldy): SEMUA lagu yang dihapus hilang dari katalog
  // dan berkasnya dibuang; yang pernah dinyanyikan tamu barisnya disimpan
  // sebagai riwayat saja (lihat backend HapusLagu).
  const [kabar, setKabar] = useState('')
  async function hapus(l: Lagu) {
    if (!confirm(`Hapus "${l.judul} — ${l.artis}" dari katalog?\nBerkas video/audio ikut dihapus (di PC karaoke & server lagu outlet). Riwayat & skor tamu tetap tersimpan.`)) return
    setGalat(''); setKabar('')
    try {
      const r = await api.delete<{ pesan: string }>(`/api/lagu/${l.id}`)
      setKabar(r.data.pesan); await muat()
    } catch (e) { setGalat(pesanGalat(e, 'Gagal menghapus lagu')) }
  }

  // 21 Okt: lirik semua lagu hasil mesin diproses ulang (LRCLIB dulu).
  async function lirikUlangSemua() {
    if (!confirm('Jadwalkan ulang lirik SEMUA lagu yang liriknya masih hasil mesin? Lirik manual tidak disentuh. PC karaoke memprosesnya satu per satu.')) return
    setGalat(''); setKabar('')
    try { const r = await api.post<{ pesan: string }>('/api/lagu/lirik-ulang-semua'); setKabar(r.data.pesan) }
    catch (e) { setGalat(pesanGalat(e, 'Gagal menjadwalkan')) }
  }

  async function ubah(l: Lagu, patch: Record<string, string>) {
    setGalat('')
    try { await api.patch(`/api/lagu/${l.id}/status`, patch); await muat() }
    catch (e) { setGalat(pesanGalat(e, 'Gagal menyimpan')) }
  }

  // Server lama (belum punya /ringkas) → jatuh ke hitungan halaman ini.
  const total = ringkas?.total ?? lagu.length
  const siap = ringkas?.siap ?? lagu.filter(l => l.status === 'siap').length
  const perluLisensi = ringkas?.perlu_lisensi ?? lagu.filter(l => l.status_lisensi !== 'aman').length
  const cocok = ringkas?.cocok ?? lagu.length
  const jmlHal = Math.max(1, Math.ceil(cocok / PER_HAL))
  const dari = cocok === 0 ? 0 : hal * PER_HAL + 1
  const sampai = Math.min(cocok, hal * PER_HAL + lagu.length)
  const angka = (n: number) => n.toLocaleString('id-ID')
  const Pager = () => jmlHal > 1 ? (
    <div className="flex items-center gap-2 text-[12px] text-text-3">
      <button disabled={hal === 0 || memuat} onClick={() => setHal(h => Math.max(0, h - 1))}
        className="px-2.5 py-1 rounded-lg bg-bg-3 border border-border text-text-2 disabled:opacity-40">‹ Sebelumnya</button>
      <span>Hal {hal + 1} / {angka(jmlHal)}</span>
      <button disabled={hal + 1 >= jmlHal || memuat} onClick={() => setHal(h => h + 1)}
        className="px-2.5 py-1 rounded-lg bg-bg-3 border border-border text-text-2 disabled:opacity-40">Berikutnya ›</button>
    </div>
  ) : null

  // Kode kategori unik lintas bahasa (pop ada di Indonesia & Inggris) untuk
  // saringan dan pengubahan massal; server melewati lagu yang bahasanya tidak
  // punya kategori itu.
  const kodeUnik = Array.from(new Map(daftarKat.filter(k => k.aktif).map(k => [k.kode, k.nama])).entries())
  const katUntuk = (bhs?: string | null) => daftarKat.filter(k => k.bahasa === bhs && k.aktif)
  async function ubahKategori(l: Lagu, kode: string) {
    try {
      await api.patch(`/api/lagu/${l.id}/kategori`, { kategori: kode === '__auto' ? '' : kode })
      await muat(); muatKat()
    } catch (e) { setGalat(pesanGalat(e, 'Gagal mengubah kategori')) }
  }
  async function terapkanMassal() {
    if (!pilih.size || !katMassal) return
    try {
      const r = await api.post<{ diubah: number; dilewati: number }>('/api/lagu/kategori-massal',
        { ids: Array.from(pilih), kategori: katMassal === '__auto' ? '' : katMassal })
      setKabar(`Kategori ${r.data.diubah} lagu diubah` + (r.data.dilewati ? ` · ${r.data.dilewati} dilewati (kategori itu tidak ada untuk bahasanya)` : ''))
      setPilih(new Set()); await muat(); muatKat()
    } catch (e) { setGalat(pesanGalat(e, 'Gagal mengubah kategori')) }
  }
  const semuaDipilih = lagu.length > 0 && lagu.every(l => pilih.has(l.id))

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <Kartu label="Total lagu" nilai={total} />
        <Kartu label="Siap tayang" nilai={siap} warna="text-emerald-400" />
        <Kartu label="Lisensi belum beres" nilai={perluLisensi}
          warna={perluLisensi > 0 ? 'text-amber-400' : 'text-text-3'} />
      </div>

      <div className="bg-bg-1 border border-border rounded-xl p-5">
        <div className="flex items-center gap-2.5 mb-4 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-3" />
            <input value={cari} onChange={e => setCari(e.target.value)}
              placeholder="Cari judul / artis" className="!pl-8 w-56" />
          </div>
          <select value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">Semua status</option>
            <option value="siap">Siap tayang</option>
            <option value="draft">Draft</option>
            <option value="nonaktif">Nonaktif</option>
          </select>
          <select value={kategori} onChange={e => setKategori(e.target.value)} title="Kategori Top ElGroup">
            <option value="">Semua kategori</option>
            {kodeUnik.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
            <option value="-">— Belum berkategori</option>
          </select>
          <span className="text-[12px] text-text-3">
            {memuat ? 'memuat…' : cocok > lagu.length ? `${angka(dari)}–${angka(sampai)} dari ${angka(cocok)} lagu` : `${angka(cocok)} lagu`}
          </span>
          <Pager />
          <div className="flex-1" />
          {bolehKelola && (
            <button onClick={lirikUlangSemua} title="Lirik hasil mesin diproses ulang: LRCLIB dulu, Whisper hanya untuk waktu"
              className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg bg-bg-3
                         border border-border text-text-2 hover:border-gold-2 transition-colors">
              <RefreshCw size={13} /> Perbarui lirik semua
            </button>
          )}
          {bolehKelola && (
            <button onClick={() => setFormBuka(true)}
              className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg bg-gold/10
                         border border-gold/30 text-gold hover:bg-gold/20 transition-colors">
              <Plus size={14} /> Tambah lagu
            </button>
          )}
        </div>

        {galat && (
          <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2 mb-3">
            {galat}
          </div>
        )}
        {kabar && (
          <div className="text-[12px] text-gold bg-gold/10 border border-gold/25 rounded-lg px-3 py-2 mb-3">{kabar}</div>
        )}
        {bolehKelola && pilih.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap text-[12px] bg-bg-2 border border-border rounded-lg px-3 py-2 mb-3">
            <b>{pilih.size} lagu dipilih</b> → kategori
            <select value={katMassal} onChange={e => setKatMassal(e.target.value)}>
              <option value="">— pilih —</option>
              {kodeUnik.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
              <option value="__auto">↺ Kembali ke otomatis</option>
            </select>
            <button disabled={!katMassal} onClick={terapkanMassal}
              className="px-3 py-1 rounded-md bg-gold/10 border border-gold/30 text-gold disabled:opacity-40">Terapkan</button>
            <button onClick={() => setPilih(new Set())} className="px-2 py-1 text-text-3">Batal</button>
          </div>
        )}

        {lagu.length === 0 && !memuat ? (
          <div className="text-[13px] text-text-3 py-12 text-center leading-relaxed">
            Katalog masih kosong.<br />
            <span className="text-[12px]">
              Lihat tab <b className="text-text-2">Chart &amp; Pesanan</b> untuk melihat lagu apa yang sedang dicari orang.
            </span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[11px] uppercase tracking-wide text-text-3">
                <tr className="border-b border-border">
                  {bolehKelola && (
                    <th className="py-2 w-8">
                      <input type="checkbox" checked={semuaDipilih} title="Pilih semua di halaman ini"
                        onChange={e => setPilih(e.target.checked ? new Set(lagu.map(l => l.id)) : new Set())} />
                    </th>
                  )}
                  <th className="text-left font-medium py-2">Judul</th>
                  <th className="text-left font-medium py-2">Artis</th>
                  <th className="text-left font-medium py-2 w-36">Asal berkas</th>
                  <th className="text-left font-medium py-2 w-40">Kategori</th>
                  <th className="text-left font-medium py-2 w-32">Lisensi</th>
                  <th className="text-left font-medium py-2 w-24">Status</th>
                  <th className="text-left font-medium py-2 w-28">Berkas</th>
                  {bolehKelola && <th className="text-left font-medium py-2 w-64">Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {lagu.map(l => (
                  <tr key={l.id} className="border-b border-border/60 hover:bg-bg-2/50">
                    {bolehKelola && (
                      <td className="py-2.5">
                        <input type="checkbox" checked={pilih.has(l.id)}
                          onChange={e => setPilih(p => { const n = new Set(p); if (e.target.checked) n.add(l.id); else n.delete(l.id); return n })} />
                      </td>
                    )}
                    <td className="py-2.5 pr-3">{l.judul}</td>
                    <td className="py-2.5 pr-3 text-text-2">{l.artis}</td>
                    <td className="py-2.5 text-[12px] text-text-3">{SUMBER[l.sumber] ?? l.sumber}</td>
                    <td className="py-2.5 pr-2">
                      {bolehKelola && katUntuk(l.bahasa_kategori).length > 0 ? (
                        <div className="flex items-center gap-1.5">
                          <select value={l.kategori ?? ''} onChange={e => ubahKategori(l, e.target.value)}
                            className="!py-0.5 !text-[12px] max-w-[130px]"
                            title={`${NAMA_BAHASA[l.bahasa_kategori ?? ''] ?? ''} — ${l.kategori_manual ? 'dipetakan tangan' : 'tebakan otomatis'}`}>
                            {!l.kategori && <option value="">—</option>}
                            {katUntuk(l.bahasa_kategori).map(k => <option key={k.kode} value={k.kode}>{k.nama}</option>)}
                            {l.kategori_manual && <option value="__auto">↺ Otomatis</option>}
                          </select>
                          {!l.kategori_manual && <span className="text-[10px] text-text-3" title="Tebakan otomatis — ubah untuk memetakan">auto</span>}
                        </div>
                      ) : (
                        <span className="text-[12px] text-text-3">
                          {daftarKat.find(k => k.bahasa === l.bahasa_kategori && k.kode === l.kategori)?.nama ?? l.kategori ?? '—'}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5">
                      <span className={`text-[11px] px-2 py-0.5 rounded-md border ${PIL_LISENSI[l.status_lisensi] ?? ''}`}>
                        {l.status_lisensi.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-2.5">
                      <span className={`text-[11px] px-2 py-0.5 rounded-md border ${PIL_STATUS[l.status] ?? ''}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="py-2.5 text-[11px] text-text-3">
                      {l.ada_video ? 'video' : '—'}
                      {' · '}
                      {l.lirik_sinkron ? 'lirik ✓' : l.ada_lirik ? 'lirik (tak sinkron)' : 'tanpa lirik'}
                    </td>
                    {bolehKelola && (
                      <td className="py-2.5">
                        <div className="flex gap-1.5">
                          {/* Selalu tampil, termasuk untuk lagu yang sudah
                              'siap'. Lagu bisa tayang tanpa lirik, tanpa trek
                              instrumen, dan tanpa melodi acuan — 'siap' cuma
                              berarti lisensinya beres, bukan bahwa fitur tamu
                              lengkap. Menyembunyikan tombolnya pada lagu siap
                              berarti tidak ada jalan memperbaikinya. */}
                          <button onClick={() => setUbahLagu(l)}
                            title="Ubah judul, artis, bahasa, genre, nada dasar, asal berkas"
                            className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-bg-3
                                       border border-border text-text-2 hover:border-gold-2 transition-colors">
                            <Pencil size={12} /> Ubah
                          </button>
                          <button onClick={() => setSiapkan(l.id)}
                            title="Lirik, trek instrumen, melodi acuan"
                            className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-bg-3
                                       border border-border text-text-2 hover:border-gold-2 transition-colors">
                            <Sliders size={12} /> Siapkan
                          </button>
                          {l.status_lisensi !== 'aman' && (
                            <button onClick={() => ubah(l, { status_lisensi: 'aman' })}
                              title="Tandai lisensi sudah beres"
                              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-bg-3
                                         border border-border text-text-2 hover:border-gold-2 transition-colors">
                              <ShieldCheck size={12} /> Lisensi OK
                            </button>
                          )}
                          {l.status !== 'siap' ? (
                            <button onClick={() => ubah(l, { status: 'siap' })}
                              className="text-[11px] px-2 py-1 rounded-md bg-emerald-500/10 border
                                         border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 transition-colors">
                              Tayangkan
                            </button>
                          ) : (
                            <button onClick={() => ubah(l, { status: 'draft' })}
                              className="text-[11px] px-2 py-1 rounded-md bg-bg-3 border border-border
                                         text-text-2 hover:border-gold-2 transition-colors">
                              Tarik
                            </button>
                          )}
                          <button onClick={() => hapus(l)} title="Hapus dari katalog"
                            className="text-[11px] px-2 py-1 rounded-md bg-red-500/10 border border-red-500/30
                                       text-red-400 hover:bg-red-500/20 transition-colors">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 flex justify-end"><Pager /></div>
          </div>
        )}
      </div>

      {formBuka && <FormLagu tutup={() => setFormBuka(false)} selesai={() => { setFormBuka(false); muat() }} />}
      {ubahLagu && <FormLagu awal={ubahLagu} tutup={() => setUbahLagu(null)} selesai={() => { setUbahLagu(null); muat() }} />}
      {/* muat() dipanggil saat panel ditutup: menyimpan lirik atau melodi
          mengubah kolom yang tampil di baris tabel ini. Tanpa itu, orang
          menyimpan lirik lalu melihat baris yang masih berbunyi "tanpa
          lirik" — dan menyimpulkan simpannya gagal. */}
      {siapkan && <Siapkan songID={siapkan} tutup={() => { setSiapkan(null); muat() }} />}
    </div>
  )
}

function Kartu({ label, nilai, warna = 'text-text' }: { label: string; nilai: number; warna?: string }) {
  return (
    <div className="bg-bg-1 border border-border rounded-xl px-4 py-3.5">
      <div className="text-[11px] uppercase tracking-wide text-text-3">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${warna}`}>{nilai.toLocaleString('id-ID')}</div>
    </div>
  )
}

// awal terisi = mode UBAH (PUT /api/lagu/:id); kosong = tambah (POST /api/lagu).
function FormLagu({ tutup, selesai, awal }: { tutup: () => void; selesai: () => void; awal?: Lagu }) {
  const [f, setF] = useState({
    judul: awal?.judul ?? '', artis: awal?.artis ?? '', bahasa: awal?.bahasa ?? 'id',
    genre: awal?.genre ?? '', nada_dasar: awal?.nada_dasar ?? '', tahun: awal?.tahun ? String(awal.tahun) : '',
    sumber: awal?.sumber ?? '', status_lisensi: awal?.status_lisensi ?? 'belum_diperiksa',
    lisensi_catatan: awal?.lisensi_catatan ?? '',
  })
  const [galat, setGalat] = useState('')
  const [simpan, setSimpan] = useState(false)

  async function kirim(e: FormEvent) {
    e.preventDefault()
    setSimpan(true); setGalat('')
    const isi = { ...f, tahun: f.tahun ? Number(f.tahun) : null }
    try {
      if (awal) {
        await api.put(`/api/lagu/${awal.id}`, isi)
        // Status lisensi punya aturan sendiri (PATCH /status) — dikirim hanya bila berubah.
        if (f.status_lisensi !== awal.status_lisensi) {
          await api.patch(`/api/lagu/${awal.id}/status`, { status_lisensi: f.status_lisensi })
        }
      } else {
        await api.post('/api/lagu', isi)
      }
      selesai()
    }
    catch (err) { setGalat(pesanGalat(err, 'Gagal menyimpan lagu')) }
    finally { setSimpan(false) }
  }

  const set = (k: string, v: string) => setF(p => ({ ...p, [k]: v }))

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-5 z-50" onClick={tutup}>
      <form onClick={e => e.stopPropagation()} onSubmit={kirim}
        className="w-full max-w-lg bg-bg-1 border border-border rounded-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">{awal ? 'Ubah lagu' : 'Tambah lagu'}</h2>
          <button type="button" onClick={tutup} className="text-text-3 hover:text-text"><X size={18} /></button>
        </div>

        <div className="grid gap-2.5 sm:grid-cols-2">
          <input required value={f.judul} onChange={e => set('judul', e.target.value)} placeholder="Judul *" />
          <input required value={f.artis} onChange={e => set('artis', e.target.value)} placeholder="Artis *" />
          <select value={f.bahasa} onChange={e => set('bahasa', e.target.value)}>
            <option value="id">Indonesia</option><option value="en">Inggris</option>
            <option value="kr">Korea</option><option value="jp">Jepang</option>
            <option value="zh">Mandarin</option><option value="xx">Lainnya</option>
          </select>
          <input value={f.genre} onChange={e => set('genre', e.target.value)} placeholder="Genre" />
          <input value={f.nada_dasar} onChange={e => set('nada_dasar', e.target.value)} placeholder="Nada dasar (mis. G)" />
          <input type="number" min="1900" max="2100" value={f.tahun} onChange={e => set('tahun', e.target.value)} placeholder="Tahun" />
        </div>

        {/* ── Asal berkas: WAJIB, tanpa nilai bawaan ──────────────────────
            Nilai bawaan di sini akan jadi jawaban yang tidak pernah diperiksa
            siapa pun — dan pertanyaan "lagu ini dari mana" akan terjawab oleh
            tebakan yang ditulis kode, bukan oleh orang yang tahu. */}
        <div className="space-y-1.5">
          <label className="text-[12px] text-text-2">Asal berkas *</label>
          <select required value={f.sumber} onChange={e => set('sumber', e.target.value)} className="w-full">
            <option value="">— pilih —</option>
            {Object.entries(SUMBER).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <p className="text-[11px] text-text-3 leading-relaxed">
            Wajib diisi dan tidak punya nilai bawaan. Ini yang membuat pertanyaan
            “lagu ini dari mana” selalu punya jawaban.
          </p>
        </div>

        <div className="space-y-1.5">
          <label className="text-[12px] text-text-2">Status lisensi</label>
          <select value={f.status_lisensi} onChange={e => set('status_lisensi', e.target.value)} className="w-full">
            <option value="belum_diperiksa">Belum diperiksa</option>
            <option value="perlu_dokumen">Perlu dokumen</option>
            <option value="aman">Aman — boleh tayang</option>
            <option value="ditolak">Ditolak</option>
          </select>
          <div className="flex items-start gap-2 text-[11px] text-amber-400/90 bg-amber-500/5
                          border border-amber-500/20 rounded-lg px-2.5 py-2 leading-relaxed">
            <ShieldAlert size={13} className="shrink-0 mt-0.5" />
            Lagu tidak bisa ditayangkan ke tamu selama status lisensinya belum “aman”.
            Itu ditolak database, bukan sekadar peringatan di layar ini.
          </div>
        </div>

        <textarea value={f.lisensi_catatan} onChange={e => set('lisensi_catatan', e.target.value)}
          placeholder="Catatan lisensi — dari siapa, kontrak nomor berapa, dsb." rows={2} className="w-full" />

        {galat && (
          <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2">
            {galat}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={tutup}
            className="text-[13px] px-4 py-2 rounded-lg bg-bg-3 border border-border text-text-2">Batal</button>
          <button type="submit" disabled={simpan}
            className="text-[13px] px-4 py-2 rounded-lg bg-gold/15 border border-gold/30 text-gold
                       hover:bg-gold/25 transition-colors disabled:opacity-50">
            {simpan ? 'Menyimpan…' : 'Simpan'}
          </button>
        </div>
      </form>
    </div>
  )
}
